import { useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';

/**
 * Creates a debounced version of a callback function that accepts parameters.
 * Unlike useDebounceFn (which is for parameterless functions reading from state/refs),
 * this hook supports passing parameters directly to the debounced function.
 *
 * Use this when:
 * - Your function needs to accept different parameters on each call
 * - You're implementing async validators that receive (value, resolve) pairs
 * - You need to pass event objects or dynamic data to the debounced function
 *
 * Use useDebounceFn when:
 * - Your function reads from React state or refs (no parameters needed)
 * - You're debouncing state updates (e.g., search box input)
 *
 * @param {Function} callback - The function to debounce. Will receive all parameters passed to debounced function.
 * @param {number} timeout - Debounce delay in milliseconds (default: 250ms)
 * @returns {Function} Debounced function that accepts parameters
 *
 * @example
 * // Async validator with parameters
 * const debouncedValidation = useDebounceFn(
 *     async (value, resolve) => {
 *         const result = await validateAPI(value);
 *         resolve(result);
 *     },
 *     500
 * );
 *
 * // Usage: pass parameters directly
 * debouncedValidation(ipAddress, resolveCallback);
 *
 * @example
 * // Event handler with parameters
 * const debouncedSearch = useDebounceFn(
 *     (searchTerm, userId) => {
 *         fetchResults(searchTerm, userId);
 *     },
 *     300
 * );
 *
 * // Usage
 * debouncedSearch('react', currentUserId);
 */
const useDebounceFn = (callback, timeout = 250) => {
    const ref = useRef(null);

    // Keep ref updated with latest callback to avoid stale closures
    useEffect(() => {
        ref.current = callback;
    }, [callback]);

    // Create debounced function that accepts parameters
    return useMemo(() => {
        const func = (...args) => {
            ref.current?.(...args);
        };

        return debounce(func, timeout);
    }, [timeout]);
};

export default useDebounceFn;
